/**
 * Date utility functions for Eastern Time handling
 * All dates in this app are assumed to be in Eastern Time
 */

/**
 * Formats a date string (YYYY-MM-DD) or Date object to a readable string
 * e.g., "Mon Jan 15 2024"
 * @param {string|Date} date - Date string in YYYY-MM-DD format or Date object
 * @returns {string} Formatted date string
 */
export function formatDateForDisplay(date) {
  if (!date) return '';

  // If it's already a YYYY-MM-DD string, parse it directly
  if (typeof date === 'string' && !date.includes('T')) {
    const [year, month, day] = date.split('-').map(Number);
    // Create date at noon to avoid timezone issues
    const dateObj = new Date(year, month - 1, day, 12, 0, 0);
    return dateObj.toDateString();
  }

  // If it's an ISO string or Date object, convert to Eastern time
  const dateObj = new Date(date);
  // Format in Eastern time
  const options = {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'America/New_York'
  };
  return dateObj.toLocaleDateString('en-US', options);
}

/**
 * Extracts date part (YYYY-MM-DD) from a date string or Date object
 * Returns the date in Eastern time
 * @param {string|Date} date - Date string or Date object
 * @returns {string} Date in YYYY-MM-DD format
 */
export function extractDateForInput(date) {
  if (!date) return '';

  // If it's already a YYYY-MM-DD string, return as-is
  if (typeof date === 'string' && !date.includes('T')) {
    return date;
  }

  // If it's an ISO string, extract just the date part
  if (typeof date === 'string' && date.includes('T')) {
    return date.substring(0, 10);
  }

  // If it's a Date object, format in Eastern time
  const dateObj = new Date(date);
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  return formatter.format(dateObj);
}
